import { cn } from "@/lib/utils";

/**
 * Vertical A–Z jump rail for long alphabetical lists (contacts-style). Renders
 * `#` (for entries starting with a digit/symbol) then A–Z; letters with no
 * entries are dimmed and non-interactive. Clicking a letter calls `onSelect`;
 * the caller scrolls the matching row into view. Pair with `letterOf` to bucket
 * each row's label. Sticky-position it beside a scrolling list.
 */

const LETTERS: string[] = ["#", ...Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ")];

/** Bucket a label to its index letter: A–Z uppercased, else "#". */
export function letterOf(label: string): string {
  const c = (label.trim()[0] ?? "").toUpperCase();
  return c >= "A" && c <= "Z" ? c : "#";
}

export interface AlphabetIndexProps {
  /** Letters that have at least one entry (the rest render dimmed). */
  available: Iterable<string>;
  /** The letter currently in view, highlighted. */
  active?: string | null;
  onSelect: (letter: string) => void;
  className?: string;
}

export function AlphabetIndex({ available, active, onSelect, className }: AlphabetIndexProps) {
  const have = available instanceof Set ? available : new Set(available);
  return (
    <nav
      aria-label="Jump to letter"
      className={cn("flex select-none flex-col items-center gap-0.5", className)}
    >
      {LETTERS.map((letter) => {
        const enabled = have.has(letter);
        const isActive = active === letter && enabled;
        return (
          <button
            key={letter}
            type="button"
            disabled={!enabled}
            aria-current={isActive ? "true" : undefined}
            onClick={() => enabled && onSelect(letter)}
            className={cn(
              "flex h-4 w-4 items-center justify-center rounded text-[10px] font-semibold leading-none transition-colors",
              enabled
                ? "cursor-pointer text-muted-foreground hover:bg-brand hover:text-white"
                : "cursor-default text-border",
              isActive && "bg-brand-light text-brand",
            )}
          >
            {letter}
          </button>
        );
      })}
    </nav>
  );
}
